import React from 'react';
import { motion } from 'framer-motion';
import {
  FaCode,
  FaServer,
  FaDatabase,
  FaCogs,
  FaRobot,
  FaMobileAlt,
  FaArrowRight,
  FaLayerGroup,
  FaShieldAlt
} from 'react-icons/fa';
import '../css/Skills.css';

const skills = [
  {
    icon: <FaCode />,
    title: 'Frontend Engineering',
    text: 'React, JavaScript, responsive interfaces, reusable components, polished UX systems, and clean visual structure.',
    tags: ['React', 'JavaScript', 'Responsive UI']
  },
  {
    icon: <FaMobileAlt />,
    title: 'Mobile Development',
    text: 'React Native, Expo Router, mobile-first architecture, native-like flows, and production-focused app experiences.',
    tags: ['React Native', 'Expo', 'Mobile UX']
  },
  {
    icon: <FaServer />,
    title: 'Backend Systems',
    text: 'FastAPI, Flask, Node.js, Express.js, REST APIs, auth flows, RBAC, and structured business logic.',
    tags: ['FastAPI', 'Node.js', 'JWT']
  },
  {
    icon: <FaDatabase />,
    title: 'Data & Persistence',
    text: 'MongoDB, Firebase, SQL, data modeling, CRUD architecture, and scalable application state handling.',
    tags: ['MongoDB', 'Firebase', 'SQL']
  },
  {
    icon: <FaRobot />,
    title: 'AI & Automation',
    text: 'LLM workflows, Ollama, voice AI, assistant logic, intent handling, and practical automation systems.',
    tags: ['LLMs', 'Ollama', 'Automation']
  },
  {
    icon: <FaCogs />,
    title: 'Product Systems',
    text: 'Architecture thinking, admin dashboards, business flows, service products, and real-world operational design.',
    tags: ['Architecture', 'Dashboards', 'Product Logic']
  }
];

const topStats = [
  {
    icon: <FaLayerGroup />,
    value: '6',
    label: 'Core skill areas'
  },
  {
    icon: <FaShieldAlt />,
    value: 'Full Stack',
    label: 'Secure product mindset'
  },
  {
    icon: <FaRobot />,
    value: 'AI + Apps',
    label: 'Practical integration'
  }
];

const headingVariants = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 45, scale: 0.96 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.07,
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};

const Skills = () => {
  return (
    <section id="skills" className="skills-section">
      <div className="skills-bg-orb orb-left" />
      <div className="skills-bg-orb orb-right" />
      <div className="skills-grid-overlay" />
      <div className="skills-watermark">SKILLS</div>

      <motion.div
        className="skills-heading-wrap"
        variants={headingVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
      >
        <span className="skills-kicker">Core Strengths</span>
        <h1>Skills</h1>
        <p>
          A combination of frontend, backend, mobile, AI, and product-building
          experience focused on useful, scalable, real-world software.
        </p>
      </motion.div>

      <motion.div
        className="skills-top-strip"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        {topStats.map((item, index) => (
          <motion.div
            key={index}
            className="skills-stat-card"
            whileHover={{ y: -4 }}
            transition={{ duration: 0.2 }}
          >
            <div className="skills-stat-icon">{item.icon}</div>
            <div>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <div className="skills-showcase-shell">
        <motion.div
          className="skills-feature-panel"
          initial={{ opacity: 0, y: 35, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8 }}
          whileHover={{
            y: -8,
            rotateX: 1.5,
            rotateY: -1.5,
            transition: { duration: 0.24 }
          }}
        >
          <div className="skills-feature-noise" />
          <div className="skills-feature-shine" />

          <div className="skills-feature-left">
            <span className="skills-feature-tag">Capability Map</span>
            <h2>Built for products, not just code.</h2>
            <p>
              My skill set is strongest where engineering meets product thinking:
              crafting interfaces, building backend logic, connecting AI flows,
              and turning ideas into systems people can actually use.
            </p>

            <div className="skills-feature-points">
              <div className="skills-point">
                <span className="skills-point-dot" />
                <span>Designing complete user and admin experiences</span>
              </div>
              <div className="skills-point">
                <span className="skills-point-dot" />
                <span>Building secure APIs and structured data systems</span>
              </div>
              <div className="skills-point">
                <span className="skills-point-dot" />
                <span>Integrating AI where it improves real workflows</span>
              </div>
            </div>

            <div className="skills-feature-link">
              <span>Full-stack + AI + product direction</span>
              <FaArrowRight />
            </div>
          </div>

          <div className="skills-feature-right">
            <motion.div
              className="skills-floating-card floating-one"
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 4.8, ease: 'easeInOut' }}
            >
              <span>Frontend Systems</span>
            </motion.div>

            <motion.div
              className="skills-floating-card floating-two"
              animate={{ y: [0, -12, 0] }}
              transition={{ repeat: Infinity, duration: 5.4, ease: 'easeInOut' }}
            >
              <span>Backend Logic</span>
            </motion.div>

            <motion.div
              className="skills-floating-card floating-three"
              animate={{ y: [0, -8, 0] }}
              transition={{ repeat: Infinity, duration: 4.2, ease: 'easeInOut' }}
            >
              <span>AI Workflows</span>
            </motion.div>

            <div className="skills-radar-shell">
              <div className="skills-radar-ring ring-1" />
              <div className="skills-radar-ring ring-2" />
              <div className="skills-radar-ring ring-3" />
              <div className="skills-radar-core" />
            </div>
          </div>
        </motion.div>

        <div className="skills-modern-grid">
          {skills.map((skill, index) => (
            <motion.div
              className="skill-modern-card"
              key={skill.title}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              whileHover={{
                y: -8,
                rotateX: 1.5,
                rotateY: index % 2 === 0 ? -1.5 : 1.5,
                transition: { duration: 0.22 }
              }}
            >
              <div className="skill-card-spotlight" />
              <div className="skill-card-shine" />

              <div className="skill-modern-icon">{skill.icon}</div>

              <div className="skill-card-head">
                <h2>{skill.title}</h2>
              </div>

              <p>{skill.text}</p>

              <div className="skill-tags-row">
                {skill.tags.map((tag, i) => (
                  <motion.span
                    key={i}
                    className="skill-tag-pill"
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.18 }}
                  >
                    {tag}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
